import React, { useEffect } from 'react';
import { Field, Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Button, Label } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { APP_DATE_FORMAT, messages } from 'app/config/constants';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { fetchCaseAppealQuery, saveCaseAppealQuery } from 'app/edozvola/cases/case-appeal/case-appeal.queries';
import { Loader } from 'app/shared/loader/loader';
import {
  fetchAppealSolutionFirstDegreeCodebookQuery,
  fetchAppealSolutionSecondDegreeCodebookQuery,
} from 'app/edozvola/codebooks/codebooks.queries';
import {PaginatedDropdown} from "edozvola-frontend-lib/dist/ui/components/paginated-dropdown";

const CaseAppealForm = () => {
  const { id } = useParams<{ id: string }>();
  const { data: initialData, isLoading: initialDataLoading } = fetchCaseAppealQuery(id);
  const { data: appealSolutionFirstDegreeOptions, isLoading: appealSolutionFirstDegreeOptionsLoading } =
    fetchAppealSolutionFirstDegreeCodebookQuery();
  const { data: appealSolutionSecondDegreeOptions, isLoading: appealSolutionSecondDegreeOptionsLoading } =
    fetchAppealSolutionSecondDegreeCodebookQuery();
  const { mutate: saveCaseAppealMutation, isPending: isCaseAppealSaving } = saveCaseAppealQuery(id);
  const dispatch = useDispatch();

  const FormikValueObserver = ({ onChange }) => {
    const { dirty } = useFormikContext();

    useEffect(() => onChange({ dirty }), [dirty, onChange]);

    return null;
  };

  const validation = Yup.object({
    appealSolutionFirstDegree: Yup.object().required(messages.MANDATORY_FIELD),
  });

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialData}
        validationSchema={validation}
        validateOnMount={true}
        onSubmit={values => saveCaseAppealMutation(values)}
      >
        {({ errors, values, isValid, dirty, submitForm, setFieldValue }) => (
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            {initialDataLoading && <Loader />}
            {!initialDataLoading && (
              <div>
                <h6 className={'form-header'}>Žalba</h6>
                <Row>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Datum dostavljene žalbe'} />
                      <Field
                        id="appealDate"
                        name="appealDate"
                        render={({ field }) => (
                          <FormikDatepicker
                            field={field}
                            setFieldValue={setFieldValue}
                            dateFormat={APP_DATE_FORMAT}
                            disabled={values?.readonly}
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Odluka prvog stupnja'} />
                      <Field
                        id="appealSolutionFirstDegree"
                        name="appealSolutionFirstDegree"
                        render={({ field }) => (
                          <PaginatedDropdown
                            placeholder={!values?.readonly && 'Odaberi odluku prvog stupnja'}
                            onItemSelect={selectedOption => setFieldValue('appealSolutionFirstDegree', selectedOption[0])}
                            selected={field.value ?? []}
                            labelKey={'value'}
                            options={appealSolutionFirstDegreeOptionsLoading ? [] : appealSolutionFirstDegreeOptions}
                            disabled={values?.readonly}
                            error={!values?.readonly && (errors.appealSolutionFirstDegree as string ?? '')}
                            showErrorMessage
                            />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Datum odluke'} />
                      <Field
                        id="appealSolutionFirstDegreeDate"
                        name="appealSolutionFirstDegreeDate"
                        render={({ field }) => (
                          <FormikDatepicker
                            field={field}
                            setFieldValue={setFieldValue}
                            dateFormat={APP_DATE_FORMAT}
                            disabled={values?.readonly}
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Odluka drugog stupnja'} />
                      <Field
                        id="appealSolutionSecondDegree"
                        name="appealSolutionSecondDegree"
                        render={({ field }) => (
                          <PaginatedDropdown
                            onItemSelect={selectedOption => setFieldValue('appealSolutionSecondDegree', selectedOption[0])}
                            selected={field.value ?? []}
                            labelKey={'value'}
                            options={appealSolutionSecondDegreeOptionsLoading ? [] : appealSolutionSecondDegreeOptions}
                            placeholder={!values?.readonly && 'Odaberi odluku drugog stupnja'}
                            disabled={values?.readonly}
                            clearButton
                            />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={4}>
                    <FormGroup>
                      <Label labelText={'Datum odluke'} />
                      <Field
                        id="appealSolutionSecondDegreeDate"
                        name="appealSolutionSecondDegreeDate"
                        render={({ field }) => (
                          <FormikDatepicker
                            field={field}
                            setFieldValue={setFieldValue}
                            dateFormat={APP_DATE_FORMAT}
                            disabled={values?.readonly}
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {!values?.readonly && (
                  <div className="case-save-button">
                    <Button
                      color={'info'}
                      label={'Spremi'}
                      size={'lg'}
                      isLoading={isCaseAppealSaving}
                      onClick={submitForm}
                      disabled={!dirty || !isValid}
                    />
                  </div>
                )}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CaseAppealForm;
